/**
 * Die Nutzungsstatistik der Web-App (HM-168, ADR 0046): welche Ansicht
 * geöffnet und welche Handlung der Oberfläche benutzt wurde.
 *
 * **Gemeldet wird ein Schlüssel, nie die Adresse.** `usageScreenOf` macht aus
 * einer Route den Schlüssel ihrer Ansicht — `meter.settings` statt
 * `/anlagen/0192…/zaehler/0193…/einstellungen`; die Kennungen bleiben im
 * Browser. Der Katalog steht in `@hausmeter/domain`; diese App darf ihn nur als
 * Typ benutzen, und `usage.test.ts` hält jeden Schlüssel dagegen.
 *
 * **Was an der API geschieht, meldet die Web-App nicht.** Ändernde Anfragen
 * zählt die API selbst; hier stehen nur Ansichten und Handlungen, die den
 * Browser nicht verlassen (Zeitraum, Anlagenwechsel, Erscheinungsbild).
 *
 * **Nichts landet auf dem Gerät.** Die Warteschlange lebt im Speicher, ohne
 * `localStorage`, ohne Cookie, ohne Kennung — was beim Schließen des Tabs noch
 * nicht hinaus ist, geht mit `keepalive` hinaus oder gar nicht.
 */
import type { UsageScreen, UsageUiAction } from "@hausmeter/domain";
import { useEffect, useRef } from "react";
import { api } from "./client.js";
import type { Route } from "./router.js";

export type UsageEventInput =
  | { readonly kind: "screen"; readonly name: UsageScreen }
  | { readonly kind: "ui"; readonly name: UsageUiAction };

/** Der Schlüssel der Ansicht, die eine Route zeigt; `null` für alles ohne Sitzung oder Inhalt. */
export function usageScreenOf(route: Route): UsageScreen | null {
  switch (route.kind) {
    case "site":
      return `site.${route.area}`;
    case "electricity":
      return "site.electricity";
    case "charging":
      return "site.charging";
    case "device":
      return `device.${route.tab}`;
    case "datapoint":
      return "datapoint";
    case "meter":
      return `meter.${route.tab}`;
    case "generation-asset":
      return "generation-asset";
    case "unit":
      return `unit.${route.tab}`;
    case "resident-unit":
      return "resident.electricity";
    case "resident-charging":
      return "resident.charging";
    case "account":
      return `account.${route.area}`;
    case "usage":
      return "account.usage";
    case "feedback":
      return "account.feedback";
    case "fleet":
      return "account.fleet";
    case "claim":
      return "claim";
    case "invitation":
      return "invitation";
    case "legal":
      return `legal.${route.document}`;
    /* Ohne Sitzung wird nicht gezählt, und `start` ist noch keine Ansicht. */
    case "start":
    case "forgot-password":
    case "reset-password":
    case "verify-email":
      return null;
  }
}

/** Wessen Ansicht eine Route ist — Anlage oder Wohnung; für den Wechsel im Umschalter. */
function placeOf(route: Route): string | null {
  if ("siteId" in route && route.kind !== "account" && route.kind !== "usage" && route.kind !== "feedback" && route.kind !== "fleet") return `site:${route.siteId}`;
  if (route.kind === "resident-unit" || route.kind === "resident-charging") return `unit:${route.unitId}`;
  return null;
}

/**
 * Was zwischen zwei Routen geschehen ist.
 *
 * Eine **neue Ansicht** ist ein Aufruf. Bleibt die Ansicht dieselbe, hat sich
 * etwas an ihr geändert — Anlage, Zeitraum, Datum oder Verlaufsfenster —, und
 * das ist eine Handlung, kein zweiter Aufruf: Sonst zählte jedes Blättern im
 * Kalender als Besuch von „Strom & Erzeugung".
 */
export function usageEventsForTransition(previous: Route | null, next: Route): UsageEventInput[] {
  const screen = usageScreenOf(next);
  if (screen === null) return [];
  if (previous === null || usageScreenOf(previous) !== screen) return [{ kind: "screen", name: screen }];

  const events: UsageEventInput[] = [];
  if (placeOf(previous) !== placeOf(next)) events.push({ kind: "ui", name: "site.switch" });
  if ("period" in previous && "period" in next && previous.period !== next.period) {
    events.push({ kind: "ui", name: "period.change" });
  } else if ("date" in previous && "date" in next && previous.date !== next.date) {
    events.push({ kind: "ui", name: "date.navigate" });
  }
  if ("range" in previous && "range" in next && previous.range !== next.range) {
    events.push({ kind: "ui", name: "range.change" });
  }
  return events;
}

/* --------------------------------------------------------------------------
 * Die Warteschlange
 * ----------------------------------------------------------------------- */

/** Wie oft gesammelt hinausgeht — öfter als alle halbe Minute sieht niemand die Zahlen. */
const FLUSH_INTERVAL_MS = 30_000;
/** So viele nimmt die API in einer Meldung (`USAGE_BATCH_MAX_EVENTS`). */
const BATCH_MAX = 50;
/** Mehr wird nicht vorgehalten; eine Statistik ist keinen Speicher wert. */
const QUEUE_MAX = 500;

let queue: UsageEventInput[] = [];
let enabled = false;

/** Ob gezählt wird — aus `CurrentUser.usageAnalytics`; abgewählt wird auch Gesammeltes verworfen. */
export function setUsageEnabled(value: boolean): void {
  enabled = value;
  if (!value) queue = [];
}

/** Meldet eine Handlung der Oberfläche — Erscheinungsbild, Seitenleiste. */
export function trackUiAction(name: UsageUiAction): void {
  enqueue([{ kind: "ui", name }]);
}

function enqueue(events: readonly UsageEventInput[]): void {
  if (!enabled || events.length === 0) return;
  queue = [...queue, ...events].slice(-QUEUE_MAX);
}

/**
 * Schickt, was gesammelt ist. **Ein Fehlschlag wird nicht wiederholt**: Die
 * Ereignisse sind weg, und das ist bei einer Statistik richtig — eine
 * Warteschlange, die bei einem Ausfall wächst und danach alles auf einmal
 * schickt, verfälscht den Tag, an dem sie es tut.
 */
export async function flushUsage(options: { readonly keepalive?: boolean } = {}): Promise<void> {
  while (enabled && queue.length > 0) {
    const batch = queue.slice(0, BATCH_MAX);
    queue = queue.slice(BATCH_MAX);
    try {
      await api.POST("/usage/events", { body: { events: batch }, keepalive: options.keepalive === true });
    } catch {
      return;
    }
  }
}

/**
 * Zählt die Ansichten einer Sitzung und schickt sie im Takt — und beim
 * Verlassen des Tabs, weil ein Browser danach keinen Takt mehr ausführt.
 */
export function useUsageTracking(route: Route, isEnabled: boolean): void {
  const previous = useRef<Route | null>(null);

  useEffect(() => {
    setUsageEnabled(isEnabled);
    if (!isEnabled) return;
    const timer = window.setInterval(() => void flushUsage(), FLUSH_INTERVAL_MS);
    const onHide = (): void => {
      if (document.visibilityState === "hidden") void flushUsage({ keepalive: true });
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [isEnabled]);

  useEffect(() => {
    enqueue(usageEventsForTransition(previous.current, route));
    previous.current = route;
  }, [route]);
}
